import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { rectSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { DashboardItem, AllowedColSpan } from 'src/types/dashboard'
import GridItem from './GridItem/GridItem'
import SortableContextWrapper from '@components/dnd-shared/SortableContextWrapper'
import DropSpacer, { SPACER_ID } from './DropSpacer'
import DragOverlayWrapper from '@components/dnd-shared/DragOverlayWrapper'
import { calculateGaps } from './calculateGaps'
import GapSpacer, { GAP_SPACER_PREFIX } from './GapSpacer'
import WidgetView from './WidgetView/WidgetView'

import './GridLayout.css'

interface GridLayoutProps {
  items: DashboardItem[]
  onReorder: (items: DashboardItem[]) => void
  onResize: (itemId: string, width: AllowedColSpan) => void
  onRemove: (itemId: string) => void
  onEdit: (itemId: string) => void
}

export default function GridLayout({
  items,
  onReorder,
  onResize,
  onRemove,
  onEdit
}: GridLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [resizePreview, setResizePreview] = useState<{
    itemId: string
    width: AllowedColSpan
  } | null>(null)

  const activeItem = activeId ? items.find((i) => i.itemId === activeId) : null

  const effectiveItems = resizePreview
    ? items.map((item) =>
        item.itemId === resizePreview.itemId
          ? { ...item, width: resizePreview.width }
          : item
      )
    : items

  const gaps = calculateGaps(effectiveItems)
  const gapsByIndex = new Map(gaps.map((g) => [g.insertIndex, g]))

  const prevLength = useRef(items.length)
  const hasLoaded = useRef(false)
  useEffect(() => {
    if (hasLoaded.current && items.length > prevLength.current) {
      const container = containerRef.current
      container?.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    }
    if (!hasLoaded.current && items.length > 0) {
      hasLoaded.current = true
    }
    prevLength.current = items.length
  }, [items.length])

  const handleResizePreview = useCallback(
    (itemId: string, width: AllowedColSpan | null) => {
      setResizePreview(width !== null ? { itemId, width } : null)
    },
    []
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null)
      const { active, over } = event
      if (!over) return
      const activeItemId = active.id as string
      if (over.id === SPACER_ID) {
        const oldIndex = items.findIndex((i) => i.itemId === activeItemId)
        if (oldIndex === items.length - 1) return
        onReorder([
          ...items.filter((i) => i.itemId !== activeItemId),
          items[oldIndex]
        ])
        return
      }
      if (String(over.id).startsWith(GAP_SPACER_PREFIX)) {
        const targetIndex = parseInt(
          String(over.id).replace(GAP_SPACER_PREFIX, ''),
          10
        )
        const oldIndex = items.findIndex((i) => i.itemId === activeItemId)
        const item = items[oldIndex]
        const without = items.filter((i) => i.itemId !== activeItemId)
        const adjustedIndex =
          oldIndex < targetIndex ? targetIndex - 1 : targetIndex
        without.splice(adjustedIndex, 0, item)
        onReorder(without)
        return
      }
      if (activeItemId === over.id) return
      const oldIndex = items.findIndex((i) => i.itemId === activeItemId)
      const newIndex = items.findIndex((i) => i.itemId === over.id)
      onReorder(arrayMove(items, oldIndex, newIndex))
    },
    [items, onReorder]
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContextWrapper
        items={items.map((i) => i.itemId)}
        strategy={rectSortingStrategy}
      >
        <div ref={containerRef} className="grid-layout">
          {effectiveItems.flatMap((item, index) => {
            const elements: React.ReactNode[] = []
            const gap = gapsByIndex.get(index)
            if (gap) {
              elements.push(
                <GapSpacer
                  key={`gap-${index}`}
                  insertIndex={gap.insertIndex}
                  width={gap.width}
                />
              )
            }

            elements.push(
              <GridItem
                key={item.itemId}
                itemId={item.itemId}
                width={item.width}
                onResize={onResize}
                onResizePreview={handleResizePreview}
                onRemove={onRemove}
                onEdit={onEdit}
                containerRef={containerRef}
                editable={item.type !== 'spacer'}
              >
                <WidgetView item={item} />
              </GridItem>
            )
            return elements
          })}
          {gapsByIndex.has(effectiveItems.length) && (
            <GapSpacer
              insertIndex={effectiveItems.length}
              width={gapsByIndex.get(effectiveItems.length)!.width}
            />
          )}
          {!gapsByIndex.has(effectiveItems.length) && <DropSpacer />}
        </div>
      </SortableContextWrapper>
      <DragOverlayWrapper>
        {activeItem ? (
          <div
            className="grid-item grid-item-overlay"
            style={{
              width: containerRef.current
                ? (containerRef.current.getBoundingClientRect().width / 12) *
                  activeItem.width
                : 'auto'
            }}
          >
            <WidgetView item={activeItem} />
          </div>
        ) : null}
      </DragOverlayWrapper>
    </DndContext>
  )
}
